import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, RefreshCw, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, Empty, Loading, Pill, Select, StatCard, Textarea } from "@/components/Ui";

const STATUSES = ["open", "in_progress", "resolved", "closed"];
const TONE = { open: "warn", in_progress: "gold", resolved: "ok", closed: "neutral" };
const FILTERS = [
  ["active", "Needs attention"],
  ["open", "Open"],
  ["in_progress", "In progress"],
  ["resolved", "Resolved"],
  ["closed", "Closed"],
  ["all", "All"],
];

const SupportReply = ({ request, onSent }) => {
  const [body, setBody] = useState("");
  const [resolve, setResolve] = useState(true);
  const [busy, setBusy] = useState(false);
  const ref = request.reference;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/support-requests/${request.id}/reply`, { body, resolve });
      setBody("");
      onSent(data);
      if (data.warning) toast.warning(data.warning);
      else toast.success(`Reply emailed to ${request.email}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-[#DFE6ED] pt-4">
      {(request.replies || []).length > 0 && (
        <div className="mb-4 space-y-3" data-testid={`support-thread-${ref}`}>
          {request.replies.map((r, i) => (
            <div key={r.sent_at || i} className="rounded-[9px] bg-[#EAF4F3] px-3.5 py-3"
              data-testid={`support-reply-item-${ref}-${i}`}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#147D78]">
                Replied by {r.by} · {(r.sent_at || "").slice(0, 16).replace("T", " ")}
                {r.delivery && r.delivery !== "sent" ? ` · email ${r.delivery} — follow up manually` : ""}
              </p>
              <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed text-[#334e68]">
                {r.body}
              </p>
            </div>
          ))}
        </div>
      )}
      <Textarea rows={3} data-testid={`support-reply-input-${ref}`} value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={`Reply to ${request.name} — this is emailed to ${request.email} and logged here.`} />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-[#334e68]">
          <input type="checkbox" data-testid={`support-resolve-toggle-${ref}`}
            className="h-4 w-4 accent-[#147D78]" checked={resolve}
            onChange={(e) => setResolve(e.target.checked)} />
          Mark as resolved when sent
        </label>
        <Btn data-testid={`support-send-reply-${ref}`} loading={busy}
          disabled={body.trim().length < 5} onClick={send} className="px-4 py-2.5 text-xs">
          <Send className="h-3.5 w-3.5" /> Send reply
        </Btn>
      </div>
    </div>
  );
};

export default function AdminSupport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/support-requests");
      setRows(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {}), [rows]);

  const visible = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "active") return rows.filter((r) => r.status === "open" || r.status === "in_progress");
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const setStatus = async (r, status) => {
    try {
      await api.put(`/admin/support-requests/${r.id}`, { status });
      setRows((list) => list.map((x) => (x.id === r.id ? { ...x, status } : x)));
      toast.success(`${r.reference} marked ${status.replace(/_/g, " ")}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading) return <Loading label="Loading support requests…" />;

  return (
    <div className="space-y-4" data-testid="admin-support">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="support-stats">
        <StatCard testId="stat-support-open" label="Open" value={counts.open || 0} sub="awaiting a first reply" />
        <StatCard testId="stat-support-progress" label="In progress" value={counts.in_progress || 0} />
        <StatCard testId="stat-support-resolved" label="Resolved" value={counts.resolved || 0} />
        <StatCard testId="stat-support-total" label="Total received" value={rows.length} />
      </div>

      <Card>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-base font-bold tracking-tight text-[#102A43]">
              Get help requests
            </h2>
            <p className="mt-1 text-sm text-[#667788]">
              Everything submitted through the Get help form. Each one was also emailed to
              projects@aditintl.com with the <strong>[ADIT CONNECT - SUPPORT]</strong> prefix. Reply
              from here and the answer is emailed to the requester and logged against the request.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Select data-testid="support-filter" value={filter}
              onChange={(e) => setFilter(e.target.value)} options={FILTERS} />
            <Btn variant="ghost" data-testid="support-refresh-btn" onClick={load} className="px-3.5 py-2 text-xs">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Btn>
          </div>
        </div>
      </Card>

      {visible.length === 0 ? (
        <Empty testId="admin-support-empty"
          title={filter === "active" ? "Nothing waiting on you" : "No requests in this view"}
          sub="New Get help submissions appear here and are emailed to projects@aditintl.com." />
      ) : (
        visible.map((r) => (
          <Card key={r.id} data-testid={`support-card-${r.reference}`} className="min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-display text-lg font-black tracking-tight text-[#102A43]">
                    {r.reference}
                  </p>
                  <Pill tone={TONE[r.status] || "neutral"} testId={`support-status-${r.reference}`}>
                    {r.status.replace(/_/g, " ")}
                  </Pill>
                  <Pill tone="neutral">{r.topic}</Pill>
                </div>
                <p className="mt-1 break-words text-sm font-semibold text-[#334e68]">{r.subject}</p>
                <p className="mt-1 break-words text-xs text-[#8598ac]">
                  {r.name}
                  {r.company ? ` · ${r.company}` : ""} · {r.email}
                  {r.phone ? ` · ${r.phone}` : ""}
                  {" · "}{(r.created_at || "").slice(0, 16).replace("T", " ")}
                </p>
                <p className="mt-1 text-xs text-[#8598ac]">
                  {r.account_email
                    ? `Signed in as ${r.account_email}`
                    : "Not signed in when submitted"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <a href={`mailto:${r.email}?subject=${encodeURIComponent(`[ADIT CONNECT - SUPPORT] ${r.subject} (${r.reference})`)}`}
                  data-testid={`support-reply-${r.reference}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#DFE6ED] px-3.5 py-2 text-xs font-semibold text-[#102A43] transition-colors hover:bg-[#eef2f7]">
                  <Mail className="h-3.5 w-3.5" /> Reply by email
                </a>
                <Select data-testid={`set-support-status-${r.reference}`} value={r.status}
                  onChange={(e) => setStatus(r, e.target.value)} options={STATUSES} />
              </div>
            </div>
            <p className="mt-4 whitespace-pre-wrap break-words border-t border-[#DFE6ED] pt-4 text-sm leading-relaxed text-[#334e68]"
              data-testid={`support-message-${r.reference}`}>
              {r.message}
            </p>
            <SupportReply request={r} onSent={(res) => setRows((list) => list.map((x) => (
              x.id === r.id
                ? { ...x, status: res.status, replies: [...(x.replies || []), res.reply] }
                : x)))} />
          </Card>
        ))
      )}
    </div>
  );
}
